<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import { megamenuSalesPath } from '~/data/megamenu'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import IconLightning from '~/assets/icons/lightning-charge-fill.svg?component'

const props = defineProps<{
  item: MegamenuNavItem
  selectedSectionIndex: number
}>()

const emit = defineEmits<{
  (e: 'selectSection', index: number): void
}>()

const localePath = useLocalePath()

function isExternal(href: string) {
  return href.startsWith('http')
}

function resolveHref(href: string) {
  return isExternal(href) ? href : localePath(href)
}

const sectionsWithContent = computed(() =>
  (props.item.sections ?? []).filter(
    (s) =>
      (s.items?.length ?? 0) > 0 ||
      (s.subSections?.length ?? 0) > 0 ||
      (s.title === 'Sales' && !s.items?.length)
  )
)

function isSalesSection(section: MegamenuSection) {
  return section.title === 'Sales'
}
</script>

<template>
  <div class="flex flex-[0_0_25%] flex-col min-w-48 border-r border-gray-200 pr-6">
    <!-- Primary "All X" link -->
    <div v-if="item.primaryLink" class="pb-2 border-b border-gray-100">
      <a
        :href="resolveHref(item.primaryLink.href)"
        class="block py-1.5 text-sm text-stone-900 no-underline hover:text-teal-600"
        :target="isExternal(item.primaryLink.href) ? '_blank' : undefined"
        :rel="isExternal(item.primaryLink.href) ? 'noopener' : undefined"
      >
        {{ item.primaryLink.label }}
      </a>
    </div>
    <!-- Section list -->
    <nav class="flex flex-col pt-2">
      <template v-for="(section, index) in sectionsWithContent" :key="section.title">
        <!-- Sales: pill style with lightning icon -->
        <a
          v-if="isSalesSection(section)"
          :href="localePath(megamenuSalesPath)"
          class="inline-flex items-center rounded-lg bg-orange-50 px-2 py-3 -mx-2 gap-1.5 text-sm font-medium text-red-500 no-underline hover:bg-amber-200"
        >
          <IconLightning class="h-4 w-4 shrink-0 text-red-500" />
          {{ section.title }}
        </a>
        <!-- Regular section row -->
        <button
          v-else
          type="button"
          class="flex items-center justify-between rounded px-2 py-3 -mx-2 text-left text-sm transition-colors"
          :class="
            selectedSectionIndex === index
              ? 'bg-teal-50/60 text-teal-800'
              : 'text-stone-900 hover:bg-gray-50 hover:text-teal-800'
          "
          @click="emit('selectSection', index)"
        >
          <span>{{ section.title }}</span>
          <IconChevronRight class="h-4 w-4 shrink-0 text-gray-800" />
        </button>
      </template>
    </nav>
  </div>
</template>
